"use client";

import * as React from "react";
import {
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Columns3, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useOrderContext } from "@/context/order-context";
import { columns, OrderBranchName, OrderPizzaDots } from "./columns";
import { DateRangeFilter } from "@/components/date-range-filter";
import { useEffect, useMemo } from "react";
import { useDashboardDateRange } from "@/context/dashboard-date-range-context";
import { ListPagination } from "@/components/list-pagination";
import { useElementWidth } from "@/hooks/use-element-width";
import { useAutoPageSize } from "@/hooks/use-auto-page-size";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { orderPizzaItems } from "@/lib/order-items";
import { formatDailyCounter } from "@/lib/counter-format";
import { cn } from "@/lib/utils";
import { useOrderPizzas } from "./hooks/use-order-pizzas";
import { shortDateTime } from "@/lib/date-format";
import { useLocalization } from "@/context/localization-context";
import { translate } from "@/i18n/translate";
import type { Order } from "@/schemas/order";

type OrdersProps = {
  getLink: (orderId: string) => string;
  useDashboardDateRange?: boolean;
  /** id of the order open in the detail; its row is highlighted */
  selectedId?: string | null;
};

/** Names of the columns in the visibility menu, in the current language. */
const columnLabels = (): Record<string, string> => ({
  daily_counter: translate("orders.columns.orderNumber"),
  sent_at: translate("orders.columns.sentAt"),
  "location.id": translate("common.branch"),
  location_id: translate("common.branch"),
  pizzas: translate("common.pizzas"),
  items: translate("orders.columns.items"),
  status: translate("common.status"),
  created_at: translate("orders.columns.createdAt"),
  id: translate("orders.columns.orderId"),
});

/** Below this list width the rows become cards. */
const CARD_LIST_MAX = 560;

const isRowControl = (target: EventTarget) =>
  target instanceof Element && !!target.closest("button, a, input, [role='menuitem']");

const orderBranchId = (order: Order) =>
  (order as unknown as { location?: { id?: string } }).location?.id;

export default function Orders({
  getLink,
  useDashboardDateRange: useDashboard = false,
  selectedId,
}: OrdersProps) {
  const navigate = useNavigate();
  // texts of the columns and the menus follow a language switch
  const { t, tp } = useLocalization();
  const { processedDateRange } = useDashboardDateRange();
  const {
    orders,
    page,
    setSortBy,
    setSortOrder,
    setPage,
    perPage,
    setPerPage,
    totalPages,
    totalOrders,
    idFilter,
    setIdFilter,
    sentAtFilterStart,
    sentAtFilterEnd,
    setSentAtFilterStart,
    setSentAtFilterEnd,
  } = useOrderContext();

  // Sync dashboard date range with orders filters when in dashboard mode
  useEffect(() => {
    if (useDashboard && processedDateRange) {
      if (processedDateRange.start) {
        setSentAtFilterStart(processedDateRange.start);
      }
      if (processedDateRange.end) {
        setSentAtFilterEnd(processedDateRange.end);
      }
    }
  }, [
    useDashboard,
    processedDateRange,
    setSentAtFilterStart,
    setSentAtFilterEnd,
  ]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      daily_counter: true,
      status: false,
      id: false,
      items: true,
      created_at: false,
      sent_at: true,
      pizzas: true,
    });

  useEffect(() => {
    if (sorting.length === 0) return;

    const sortId = sorting.length ? sorting[0].id : "sent_at";
    setSortBy(sortId === "daily_counter" ? "data.daily_counter" : sortId);
    setSortOrder(sorting.length && sorting[0].desc ? "desc" : "asc");
  }, [sorting]);

  // verdicts of every pizza on the page in one request
  const pizzaIds = useMemo(
    () => (orders ?? []).flatMap((order) => orderPizzaItems(order).map((item) => item.id)),
    [orders],
  );
  const { pizzasById } = useOrderPizzas(pizzaIds);

  const table = useReactTable({
    data: orders ?? [],
    columns: columns(pizzasById),
    pageCount: totalPages,
    manualPagination: true,
    manualSorting: true,
    state: {
      sorting,
      columnVisibility,
      pagination: {
        pageIndex: page,
        pageSize: perPage,
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
  });

  const openOrder = (orderId: string) => navigate(getLink(orderId));

  const [listRef, listWidth] = useElementWidth();
  // the selected order's row stays in view inside the list (the page itself never scrolls for it)
  const scrollRef = React.useRef<HTMLDivElement & HTMLUListElement>(null);
  useEffect(() => {
    const box = scrollRef.current;
    const row = box?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!box || !row) return;
    const boxRect = box.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const headerSpace = 40;
    if (rowRect.top < boxRect.top + headerSpace) {
      box.scrollTop -= boxRect.top + headerSpace - rowRect.top;
    } else if (rowRect.bottom > boxRect.bottom) {
      box.scrollTop += rowRect.bottom - boxRect.bottom + 8;
    }
  }, [selectedId, orders]);
  const cardList = listWidth > 0 && listWidth < CARD_LIST_MAX;
  // "Auto" page size: a page is as long as the window shows (default), or a fixed number picked below
  const [autoPageSize, setAutoPageSize] = useLocalStorageState(
    "pizza-admin:order-list-auto-page-size:v1",
    true,
  );
  useAutoPageSize({
    enabled: autoPageSize,
    boxRef: scrollRef,
    rowSelector: "[data-order-row]",
    fallbackRowHeight: cardList ? 57 : 49,
    reserved: cardList ? 0 : 36,
    perPage,
    setPerPage,
    hasRows: (orders?.length ?? 0) > 0,
    layoutKey: `${cardList}:${JSON.stringify(columnVisibility)}`,
  });
  const rows = table.getRowModel().rows;
  const labels = columnLabels();

  return (
    <div ref={listRef} className="flex h-full min-h-0 w-full flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 basis-40">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t("orders.toolbar.searchPlaceholder")}
            value={idFilter}
            onChange={(event) => setIdFilter(event.target.value.trim())}
            className="h-9 pl-8 pr-8"
            aria-label={t("orders.toolbar.searchLabel")}
          />
          {idFilter && (
            <button
              type="button"
              onClick={() => setIdFilter("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t("orders.toolbar.clearSearch")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {/* Sent At Range Filter - Hide when using dashboard date range */}
        {!useDashboard && (
          <DateRangeFilter
            from={sentAtFilterStart}
            to={sentAtFilterEnd}
            onChange={(from, to) => {
              setSentAtFilterStart(from);
              setSentAtFilterEnd(to);
            }}
            className="min-w-0 flex-1 basis-40 sm:w-48 sm:flex-none"
          />
        )}
        {!cardList && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto h-9">
                <Columns3 className="h-4 w-4" />
                <span className="hidden xl:inline">{t("common.columns")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {labels[column.id] ?? column.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {cardList ? (
        <ul ref={scrollRef} className="min-h-0 flex-1 divide-y overflow-auto rounded-lg border">
          {orders?.length ? (
            orders.map((order) => {
              const current = order.id === selectedId;
              return (
                <li key={order.id} data-order-row="">
                  <button
                    type="button"
                    onClick={() => openOrder(order.id)}
                    aria-current={current ? "true" : undefined}
                    className={cn(
                      "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors active:bg-muted",
                      current
                        ? "bg-secondary shadow-[inset_3px_0_0_hsl(var(--primary))]"
                        : "hover:bg-muted/50",
                    )}
                  >
                    <span className="w-14 shrink-0 font-mono text-base font-semibold">
                      {formatDailyCounter(order.daily_counter ?? order.formatted_id)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">
                        <OrderBranchName branchId={orderBranchId(order)} />
                      </div>
                      <div className="text-xs tabular-nums text-muted-foreground">
                        {shortDateTime(order.sent_at)} · {tp("count.items", order.items?.length ?? 0)}
                      </div>
                    </div>
                    <OrderPizzaDots order={order} pizzasById={pizzasById} />
                  </button>
                </li>
              );
            })
          ) : (
            <li className="px-4 py-10 text-center text-sm text-muted-foreground">
              {orders ? t("orders.list.empty") : t("common.loading")}
            </li>
          )}
        </ul>
      ) : (
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto rounded-lg border">
          <Table containerClassName="overflow-visible">
            <TableHeader className="sticky top-0 z-20 bg-card shadow-[inset_0_-1px_0_hsl(var(--border))] [&_tr]:border-b-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="h-9 whitespace-nowrap">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {rows.length ? (
                rows.map((row) => {
                  const current = row.original.id === selectedId;
                  return (
                    <TableRow
                      key={row.id}
                      data-order-row=""
                      data-state={current ? "selected" : undefined}
                      tabIndex={0}
                      aria-current={current ? "true" : undefined}
                      className={cn(
                        "cursor-pointer outline-none focus-visible:bg-muted/60",
                        current && "bg-secondary shadow-[inset_3px_0_0_hsl(var(--primary))]",
                      )}
                      onClick={(event) => {
                        if (!isRowControl(event.target)) openOrder(row.original.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && event.target === event.currentTarget) {
                          openOrder(row.original.id);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-2">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={table.getVisibleLeafColumns().length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {orders ? t("orders.list.empty") : t("common.loading")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        perPage={perPage}
        onPerPageChange={setPerPage}
        total={totalOrders}
        unit="orders"
        autoPageSize={autoPageSize}
        onAutoPageSizeChange={setAutoPageSize}
      />
    </div>
  );
}
